import { FormState } from 'final-form';
import { observer } from 'mobx-react';
import React, { FunctionComponent, ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { getEventTaskFactory } from '~admin/interactor/event/perform-get-event';
import { updateEventTaskFactory } from '~admin/interactor/event/update-event';
import { EventData } from '~admin/model/event-data';
import { Paths } from '~admin/route';
import { useCreateEditEventFlowStore } from '~admin/store/create-edit-event-flow-store-provider';
import { EventDetails } from '~admin/view/components/details/event-details/event-details.component';
import { EventFormData } from '~admin/view/components/details/event-form-data';
import { getEditEventInitialValues } from '~admin/view/components/edit-event/helper/edit-event-initial-values.helper';
import { editEventDetailsValidators } from '~admin/view/components/edit-event/helper/edit-event-validators.helper';
import { Wizard } from '~admin/view/components/ui/form/wizard.component';
import { Column } from '~common/components/column.component';
import { Title } from '~common/components/title.component';
import { useSuccessReaction } from '~common/hook/use-reaction';
import { useTask } from '~common/hook/use-task';
import { TaskObserver } from '~web/view/components/core/task-observer.component';

export interface EventUpdateFormData extends EventFormData {
  id: number;
}

export interface CreateEventProps {
  eventId?: string;
}

export const EditEvent: FunctionComponent<CreateEventProps> = observer(({ eventId }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const editEventFlowStore = useCreateEditEventFlowStore();
  const getEventTask = useTask(getEventTaskFactory);
  const updateEvent = useTask(updateEventTaskFactory);

  useEffect((): void => {
    getEventTask(Number(eventId));
  }, []);

  useSuccessReaction(getEventTask, (result: EventData) => {
    editEventFlowStore.setEventId(result.id);
  });

  useSuccessReaction(updateEvent, (): void => {
    history.push(Paths.tool.events);
  });

  const onFormChange = (formState: FormState<EventFormData>): void => {
    editEventFlowStore.updateState(formState);
  };

  const onSubmit = (values: EventFormData): void => {
    updateEvent(editEventFlowStore.eventId, values);
  };

  return (
    <Container>
      <Title>{t('editEvent.title')}</Title>
      <TaskObserver task={getEventTask}>
        {(result: EventData): ReactNode => (
          <Wizard
            onSubmit={onSubmit}
            submitButtonText={t('editEvent.editButton')}
            initialValues={getEditEventInitialValues(result)}
            onFormChange={onFormChange}
            task={updateEvent}
          >
            <EventDetails validate={editEventDetailsValidators} />
          </Wizard>
        )}
      </TaskObserver>
    </Container>
  );
});

const Container = styled(Column)`
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  padding: 48px 35px 0;
  max-width: 950px;
`;



// WEBPACK FOOTER //
// ./view/components/edit-event/edit-event.component.tsx